import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateIngestionConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateIngestionConfigInput {
  Id: string;
  OriginalTimestamp: string;
  // The config blob consumers hold is a parsed JSON object (`JSON.parse` /
  // spread literal); the tRPC procedure input is the shared `JSONB` type. The
  // runtime values are identical, so the public input takes the loose shape and
  // we narrow to `JSONB` at the single mapInput boundary below.
  IngestionConfig?: Record<string, unknown> | null;
  // Write-only: stored in Secrets Manager server-side, never returned.
  ApiKey?: string | null;
}

export const useUpdateIngestionConfig = createMutationHook({
  name: 'updateIngestionConfig',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.ingestionConfig.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateIngestionConfigInput) => ({
    Id: input.Id,
    OriginalTimestamp: input.OriginalTimestamp,
    // Loose parsed-JSON object → shared JSONB; identical at runtime.
    IngestionConfig: (input.IngestionConfig ?? null) as JSONB | null,
    ApiKey: input.ApiKey ?? null,
  }),
  invalidate: (trpc) => [trpc.frontend.ingestionConfig.getAll.queryKey()],
  mapResponse: (result: { Id: string }): UpdateIngestionConfigMutation => ({
    updateChildIngestionConfig: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateIngestionConfigDocument,
  graphqlVariables: (input: UpdateIngestionConfigInput) => ({
    object: {
      Id: input.Id,
      IngestionConfig: input.IngestionConfig ?? undefined,
      OriginalTimestamp: input.OriginalTimestamp,
      ApiKey: input.ApiKey ?? undefined,
    },
  }),
  evictFields: ['ingestion_config'],
});
